import { RefObject, useState } from "react";

import { usePointerDrag } from "./usePointerDrag";

export type ResizeEdge = "right" | "bottom" | "corner" | "left";

const MIN_WIDTH = 400;
const MIN_HEIGHT = 240;
const DOCKED_WIDTH = 640;
// The margins the panel's CSS caps keep free (max-w/max-h of the viewport less
// 2rem floating, 1rem docked). A size past its cap would render smaller than
// set, and dragging the panel is clamped by the size set.
const FLOATING_MARGIN = 32;
const DOCKED_MARGIN = 16;

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

type FloatingSize = {
  width: number;
  /** Unset: as tall as the content (up to the cap) until first resized. */
  height?: number;
};

/**
 * Sizes for a floating panel, resized from its right and bottom edges and the
 * corner between them, and for the docked panel, resized from its left edge.
 * Pointer-based like ResizablePanels. A resize starts from the panel's size on
 * screen, and the floating edges stop at the viewport's.
 */
export const usePanelResize = (
  initialWidth: number,
  panelRef: RefObject<HTMLElement | null>
) => {
  const [floatingSize, setFloatingSize] = useState<FloatingSize>({
    width: initialWidth,
  });
  const [dockedWidth, setDockedWidth] = useState(DOCKED_WIDTH);
  const followPointer = usePointerDrag();

  const startResize = (edge: ResizeEdge) => (e: React.PointerEvent) => {
    const panel = panelRef.current;
    if (!panel) return;
    e.preventDefault();

    const start = panel.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const { innerWidth, innerHeight } = window;

    followPointer((ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;

      if (edge === "left") {
        // Docked on the right, so dragging left widens it.
        setDockedWidth(
          clamp(start.width - dx, MIN_WIDTH, innerWidth - DOCKED_MARGIN)
        );
        return;
      }
      setFloatingSize((size) => ({
        width:
          edge === "bottom"
            ? size.width
            : clamp(
                start.width + dx,
                MIN_WIDTH,
                Math.min(innerWidth - start.left, innerWidth - FLOATING_MARGIN)
              ),
        height:
          edge === "right"
            ? size.height
            : clamp(
                start.height + dy,
                MIN_HEIGHT,
                Math.min(innerHeight - start.top, innerHeight - FLOATING_MARGIN)
              ),
      }));
    });
  };

  return { floatingSize, dockedWidth, startResize };
};
